import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { MenuLink } from '@uva-glass/component-library';

import type { CSSProperties } from 'react';

import styles from './Header.module.css';

import UvALogo from 'assets/images/UvALogo.svg';
import { HelpLink, LanguageToggle, SigninOrSignout } from 'components';
import { useDebounce } from 'hooks/useDebounce';
import { useScrollableHeader } from 'providers/ScrollableHeaderProvider';
import {
  APP_ROOT,
  AUTHORISATIONS,
  AUTHORISATIONS_OVA,
  COURSE_REGISTRATION,
  EDUCATION_PREPARATION,
  STUDENTS,
} from 'routes';
import { ApplicationModule } from 'types/__generated__';
import { useProfile } from 'providers/UserProfileProvider';

const DELAY = 50;

export const Header = () => {
  const {
    addHeaderHeight,
    scrollThreshold,
    classNameSuffix,
    animationClass,
    headerRoom,
    setHeaderRoom,
    headerTop,
    setHeaderTop,
  } = useScrollableHeader();
  const { t } = useTranslation('common', { keyPrefix: 'header' });
  const headerRef = useRef<HTMLDivElement>(null);
  const headerBottom = useRef(0);
  const headerHeight = useRef(0);
  const profile = useProfile();

  const setScrollDimensions = () => {
    setHeaderRoom(headerHeight.current);
    setHeaderTop(headerBottom.current - headerHeight.current);
    addHeaderHeight(headerBottom.current);
  };

  const debounceSetScrollDimensions = useDebounce(setScrollDimensions, DELAY);

  useEffect(() => {
    headerBottom.current = headerRef.current?.getBoundingClientRect().bottom || 0;
    headerHeight.current = headerRef.current?.getBoundingClientRect().height || 0;

    setScrollDimensions();

    window.addEventListener('resize', debounceSetScrollDimensions);

    return () => {
      window.removeEventListener('resize', debounceSetScrollDimensions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={clsx(
        styles['app-header'],
        styles[`app-${classNameSuffix}`],
        animationClass && styles[`app-${animationClass}`]
      )}
      ref={headerRef}
      role="banner"
      style={
        {
          '--scroll-threshold': `${scrollThreshold}px`,
          '--header-room': `${headerRoom}px`,
          '--header-top': `${headerTop}px`,
        } as CSSProperties
      }
    >
      <div className={styles['app-header__menu']}>
        <Link to={APP_ROOT}>
          <img src={UvALogo} alt="" className={styles['app-header-logo']} />
        </Link>
        {profile !== null && (
          <nav className={styles['app-header__navigation']}>
            {profile.enabledModules.includes(ApplicationModule.Facet) && (
              <MenuLink to={EDUCATION_PREPARATION}>{t('navigation.education-preparation')}</MenuLink>
            )}
            {profile.enabledModules.includes(ApplicationModule.Prism) && (
              <MenuLink to={COURSE_REGISTRATION}>{t('navigation.course-registration')}</MenuLink>
            )}
            {profile.enabledModules.includes(ApplicationModule.Quartz) && (
              <MenuLink to={STUDENTS}>{t('navigation.students')}</MenuLink>
            )}
            {profile.enabledModules.includes(ApplicationModule.GobletLegacy) && (
              <MenuLink to={AUTHORISATIONS}>{t('navigation.authorisations')}</MenuLink>
            )}
            {profile.enabledModules.includes(ApplicationModule.Goblet) && (
              <MenuLink to={AUTHORISATIONS_OVA}>{t('navigation.authorisations-ova')}</MenuLink>
            )}
          </nav>
        )}
      </div>
      <div className={styles['app-header__actions']} id="app-header-actions" tabIndex={-1}>
        <LanguageToggle />
        <HelpLink />
        <SigninOrSignout />
      </div>
    </div>
  );
};
